import React from 'react';
import Link from 'next/link';
import { ArrowLink, Chip, Heading, Text } from '@/components/ds';
import type { NewsArticleCard as NewsArticleCardData } from '@/api/news/types';
import { formatNewsDate, newsArticlePath } from '@/lib/news/newsUtils';
import { cn } from '@/lib/twUtils';

interface NewsArticleCardProps {
  article: NewsArticleCardData;
  /** `featured`: l'articolo d'apertura dell'indice, a tutta larghezza e col titolo grande */
  variant?: 'default' | 'featured';
  className?: string;
}

const Meta = ({ article }: { article: NewsArticleCardData }) => (
  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[0.8125rem] text-ink-subtle">
    <time dateTime={article.published_at}>{formatNewsDate(article.published_at)}</time>
    <span aria-hidden>·</span>
    <span>{article.reading_minutes} min di lettura</span>
  </div>
);

/**
 * Card di un articolo. Niente immagini, per scelta: la sezione per ora è solo
 * testo (la copertina resta nei dati e serve all'anteprima di condivisione).
 * Tutta la superficie è un link (lo `::after` del titolo
 * la copre), ma il link vero resta uno solo, sul titolo: uno screen reader non
 * legge tre volte la stessa destinazione.
 */
const NewsArticleCard = ({ article, variant = 'default', className }: NewsArticleCardProps) => {
  const href = newsArticlePath(article.slug);
  const featured = variant === 'featured';

  return (
    <article
      className={cn(
        'group relative flex overflow-hidden rounded-ds-xl border border-edge bg-white transition-[transform,border-color] duration-200 hover:-translate-y-[3px] hover:border-edge-strong motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        'flex-col',
        className
      )}
    >
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          featured
            ? 'p-5 sm:p-[clamp(1.5rem,3vw,2.5rem)]'
            : 'p-5 sm:p-[clamp(1.375rem,2.4vw,1.75rem)]'
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone="tag" size="xs">
            {article.category.name}
          </Chip>
        </div>

        <Heading
          as="h3"
          size={featured ? 'md' : 'xs'}
          className={cn('mt-3.5', featured && 'max-w-[32ch]')}
          balance={featured}
        >
          <Link
            href={href}
            className="rounded-sm outline-none after:absolute after:inset-0 after:content-[''] focus-visible:after:rounded-ds-xl focus-visible:after:ring-2 focus-visible:after:ring-accent"
          >
            {article.title}
          </Link>
        </Heading>

        <Text size={featured ? 'intro' : 'body'} className={cn('mt-3', featured && 'max-w-[72ch]')}>
          {article.excerpt}
        </Text>

        <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-6">
          <Meta article={article} />
          <ArrowLink asText>Leggi</ArrowLink>
        </div>
      </div>
    </article>
  );
};

export default NewsArticleCard;
